import {
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";

const FADE_PX = 28;

interface ScrollFadeProps extends HTMLAttributes<HTMLElement> {
  as?: "div" | "ul";
  children: ReactNode;
}

/**
 * A horizontally scrolling strip (tabs, chip rows) that fades the edge that
 * still has content, so clipped items read as "scroll for more" rather than a
 * bug. Fades only while the strip actually overflows, so it is inert once the
 * content fits (e.g. at `lg` where the strip becomes a list).
 */
export function ScrollFade({
  as: Tag = "div",
  className,
  style,
  children,
  onScroll,
  ...props
}: ScrollFadeProps) {
  const ref = useRef<HTMLElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const start = el.scrollLeft > 1;
    const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((prev) =>
      prev.start === start && prev.end === end ? prev : { start, end },
    );
  }, []);

  // Content can change without the strip resizing (tabs added, counts grow).
  useEffect(measure);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  const mask =
    edges.start || edges.end
      ? `linear-gradient(to right, ${edges.start ? "transparent" : "#000"} 0, #000 ${FADE_PX}px, #000 calc(100% - ${FADE_PX}px), ${edges.end ? "transparent" : "#000"} 100%)`
      : undefined;

  const merged: CSSProperties = {
    ...style,
    maskImage: mask,
    WebkitMaskImage: mask,
  };

  return (
    <Tag
      {...props}
      ref={ref as never}
      className={cn("overflow-x-auto", className)}
      style={merged}
      onScroll={(e) => {
        measure();
        onScroll?.(e);
      }}
    >
      {children}
    </Tag>
  );
}
